import type {
  ResponsiveValue,
  ShadowProps,
  TypographyProps,
} from 'styled-system';
import { shadow, typography } from 'styled-system';

import type {
  GridItemProps,
  TextDecorationProps,
  TextRenderingProps,
  TextTransformProps,
  TextWrapProps,
  UserSelectProps,
  WhiteSpaceProps,
} from '../extensions';
import {
  gridItem,
  textDecoration,
  textRendering,
  textTransform,
  textWrap,
  userSelect,
  whiteSpace,
} from '../extensions';
import styledFactory from '../styledFactory';
import variantFactory from '../variantFactory';

export interface Props
  extends GridItemProps,
    ShadowProps,
    TextDecorationProps,
    TextRenderingProps,
    TextTransformProps,
    TextWrapProps,
    TypographyProps,
    UserSelectProps,
    WhiteSpaceProps {
  variant?: ResponsiveValue<string>;
  __textCommon__?: 'common';
  __textComponentCommon__?: 'common';
}

type Target = 'div' | 'p' | 'span' | 'strong' | 'em' | 'small';

const createTextComponent = (
  target: Target,
  displayName: string,
  variantKey?: string,
  css?: string,
) => {
  const Component = styledFactory<Props, Target>(
    target,
    displayName,
    [
      variantFactory('components.text', { prop: '__textCommon__' }),
      variantKey != null &&
        variantFactory(`components.text.${variantKey}`, {
          prop: '__textComponentCommon__',
        }),
      variantKey != null &&
        variantFactory(`components.text.${variantKey}.variants`),
    ].filter(Boolean),
    [
      shadow,
      textDecoration,
      textRendering,
      textTransform,
      textWrap,
      typography,
      userSelect,
      whiteSpace,
      displayName === 'Text' && gridItem,
    ].filter(Boolean),
    css,
    {
      variant: variantKey != null ? 'default' : undefined,
      __textCommon__: 'common',
      __textComponentCommon__: 'common',
    },
  );

  return Component;
};

/**
 * Components to render styled text elements.
 */
const Text = createTextComponent('div', 'Text', 'text');

// TODO - move css to globalStyles? themes? BaseTheme?
const Root = createTextComponent(
  'div',
  'TextRoot',
  'root',
  `
    font-feature-settings: 'liga', 'kern';
    text-rendering: optimizeSpeed;
  `,
);

const Paragraph = createTextComponent('p', 'TextParagraph', 'p');

const Inline = createTextComponent('span', 'TextInline', 'inline');

const Strong = createTextComponent('strong', 'TextStrong', 'strong');

const Em = createTextComponent('em', 'TextEmphasized', 'em');

const Small = createTextComponent('small', 'TextSmall', 'small');

export default Object.assign(Text, {
  root: Root,
  p: Paragraph,
  inline: Inline,
  span: Inline,
  strong: Strong,
  em: Em,
  small: Small,
});
